<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { getUserCallback, type UserController } from '@/lib/util/store';
import { computed, onMounted, ref, toRefs } from 'vue';
import { ButlerbotLogin } from '@/components/custom/Login';
import { Skeleton } from '@/components/ui/skeleton';

export type PlatformInfo = {
    iconUrl: string;
    name: string;
    description: string;
};

const props = defineProps<{
    user?: UserController
    fetchUser?: boolean
    isOffline?: boolean
    platform: PlatformInfo
}>();
const platform = props.platform;

const { user, isOffline } = toRefs(props);
const isLoading = ref(user ? false : true);
const currentPath = ref<string | undefined>(undefined);

onMounted(async () => {
    // get current path
    currentPath.value = window.location.pathname + window.location.search + window.location.hash;

    // get user
    if (!props.fetchUser) return;
    isLoading.value = true;
    getUserCallback((_user, err) => {
        isLoading.value = false;
        user.value = _user;

        if (err && err == "offline") isOffline.value = true;
        else if (err) console.error(err);
    }, { minified: false })
})

// CONTEXT AND DESCRIPTORS
const profiles = {
    discord: computed(() => user.value && user.value.discordProfile),
    patreon: computed(() => user.value && user.value.patreonProfile)
}
const profile = computed(() => profiles[platform.name.toLowerCase() as keyof typeof profiles] || undefined);

const isLoggedIn = computed(() => user.value !== undefined);
const isConnected = computed(() => profile.value?.value !== undefined);
const connnectAction = computed(() => isConnected.value ? "Disconnect" : "Connect");
const connectActionDiscriptor = computed(() => isConnected.value ? "from" : "to");
const connectAction = computed(() => isConnected.value ? "logout" : "login");
</script>

<template>
    <div class="tw-contents">
        <!-- LOGIN -->
        <div class="step">
            <p>1. Login to ButlerBot</p>
            <ButlerbotLogin v-if="isOffline" disabled class="force_button" />
            <Skeleton v-else-if="isLoading" class="force_button tw-w-48 tw-h-10" />
            <ButlerbotLogin v-else :is-logged-in="user !== undefined" :redirect="`${currentPath}`"
                class="force_button" />
        </div>

        <!-- CONNECT -->
        <div class="step">
            <p>
                2. {{ connnectAction }} {{ platform.name }} {{ connectActionDiscriptor }} your ButlerBot account
            </p>
            <template v-if="isOffline">
                <Button style="font-size: 16px; text-transform: capitalize" size="lg"
                    :variant="isConnected ? 'destructive' : 'highlight'" disabled>
                    <img :src="platform.iconUrl" alt="icon" class="tw-w-5 tw-h-5 tw-mr-2" />
                    {{ connnectAction }} {{ platform.name }}
                </Button>
            </template>
            <template v-else-if="isLoading">
                <Skeleton class="force_button tw-w-48 tw-h-10" />
            </template>
            <template v-else-if="!isLoggedIn">
                <Button style="font-size: 16px; text-transform: capitalize" size="lg"
                    :variant="isConnected ? 'destructive' : 'highlight'" disabled>
                    <img :src="platform.iconUrl" alt="icon" class="tw-w-5 tw-h-5 tw-mr-2" />
                    {{ connnectAction }} {{ platform.name }}
                </Button>
            </template>
            <template v-else>
                <Button style="font-size: 16px; text-transform: capitalize" size="lg"
                    :variant="isConnected ? 'destructive' : 'highlight'" as-child>
                    <a :href="`/${connectAction}/${props.platform.name}?redirect=${currentPath}`">
                        <img :src="platform.iconUrl" alt="icon" class="tw-w-5 tw-h-5 tw-mr-2" />
                        {{ connnectAction }} {{ platform.name }}
                    </a>
                </Button>
            </template>
        </div>
    </div>
</template>

<style scoped>
.step {
    margin-top: 10px;
    margin-bottom: 10px;
    width: 100%;
}

.step button,
.step .force_button,
.step>a {
    margin-top: 10px;
    margin-left: 16px;
    color: rgb(17, 17, 17);
}
</style>